import {type AlphaCatalogue} from '@online-offer/shared';

import {
  OnlineOfferAlternative,
  OnlineOfferAlternativesResponseBody,
} from '../api/types/onlineOfferAlternativesTypes';

const PASSENGER_CAR = 'VEHICLETYPE_PASSENGER_CAR';

/**
 * Label of a catalogue key, like `getAlphaCatalogueLabel` with the key itself as the fallback.
 * An alternative carries no vehicle type, so a passenger-car property wins over the others.
 */
const getLabel = (
  alphaCatalogue: AlphaCatalogue,
  propertyName: string,
  key: string | null
): string | null => {
  if (key === null) {
    return null;
  }
  const properties = alphaCatalogue.filter((property) => property.property_name === propertyName);
  const ordered = [
    ...properties.filter((property) => property.vehicle_type === PASSENGER_CAR),
    ...properties.filter((property) => property.vehicle_type !== PASSENGER_CAR),
  ];
  for (const property of ordered) {
    const label = property.keys.find((candidate) => candidate.const_key === key)?.labels?.[0]
      ?.label;
    if (label) {
      return label;
    }
  }

  return key;
};

const localizeAlternative = (
  alternative: OnlineOfferAlternative,
  alphaCatalogue: AlphaCatalogue
): OnlineOfferAlternative => ({
  ...alternative,
  specification: {
    ...alternative.specification,
    drive: getLabel(alphaCatalogue, 'drive', alternative.specification.drive),
    bodyType: getLabel(alphaCatalogue, 'car_style', alternative.specification.bodyType),
    exteriorColor: getLabel(alphaCatalogue, 'body_color', alternative.specification.exteriorColor),
    interiorColor: getLabel(
      alphaCatalogue,
      'interior_color',
      alternative.specification.interiorColor
    ),
  },
  // Stock equipment arrives as feature codes; configuration equipment is already named.
  equipment: alternative.equipment.map((item) => ({
    ...item,
    name: getLabel(alphaCatalogue, 'feature', item.name) ?? item.name,
  })),
});

/**
 * Stock alternatives carry catalogue codes (FEATURE_*, DRIVE_*, CARSTYLE_*, COLOR_*, INTERIORCOLOR_*)
 * that the legacy offer sections translate with the same catalogue; values that are no code of
 * that property (configuration labels) come back unchanged.
 */
export const localizeAlternativeCodes = (
  offerAlternatives: OnlineOfferAlternativesResponseBody,
  alphaCatalogue: AlphaCatalogue | undefined
): OnlineOfferAlternativesResponseBody =>
  alphaCatalogue
    ? {
        ...offerAlternatives,
        alternatives: offerAlternatives.alternatives.map((alternative) =>
          localizeAlternative(alternative, alphaCatalogue)
        ),
      }
    : offerAlternatives;
